import {
  Typography,
  TextField,
  InputBaseComponentProps,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  SvgIconProps,
  Stack
} from '@mui/material'
import React from 'react'
import { Container } from 'src/ui'
import * as S from './styles'
import { LoadingButton } from '@mui/lab'
import useRequest from './Request.State'
import NumberFormat from 'react-number-format'
import { formatNumber, trimId } from 'src/common/helpers'
import RequestInitiated from './RequestInitiated'
import ConfirmRequest from './ConfirmRequest'
import RequestLoading from './RequestLoading'
import { FlagIcon, FromToIcon, QCADIcon } from 'src/common/assets'
import { ExpandMore } from '@mui/icons-material'
import { ChainAsset, ChainName, FiatCurrency, RequestType } from 'src/common/static'
import { CHAIN_NATIVE_TOKEN_ICON } from 'src/common/static/chain/native-token-icon'
import PaymentInstructions from './PaymentInstructions'
import BurnInstructions from './BurnInstructions'

interface Asset {
  name: ChainAsset
  Icon: React.FC<SvgIconProps<'svg', Record<string, unknown>>>
}

interface Currency {
  name: FiatCurrency
  Icon: React.FC<SvgIconProps<'svg', Record<string, unknown>>>
}

interface Chain {
  name: ChainName
  displayName: string
  Icon: React.FC<SvgIconProps<'svg', Record<string, unknown>>>
}

const assets = [
  {
    name: ChainAsset.QCAD,
    Icon: QCADIcon
  }
]

const chains: Chain[] = [
  {
    name: ChainName.ETHEREUM,
    displayName: 'ETH',
    Icon: CHAIN_NATIVE_TOKEN_ICON[ChainName.ETHEREUM]
  },
  {
    name: ChainName.BASE,
    displayName: 'BASE',
    Icon: CHAIN_NATIVE_TOKEN_ICON[ChainName.BASE]
  }
]

const fiatCurrencies = [
  {
    name: FiatCurrency.CAD,
    Icon: FlagIcon
  }
]

interface RequestProps {
  type: RequestType
}

const Request: React.FC<RequestProps> = ({ type }: RequestProps) => {
  const {
    value,
    asset,
    selectedChain,
    errors,
    modals,
    order,
    loading,
    anchorElAsset,
    anchorElChain,
    fiatCurrency,
    anchorElFiatCurrency,
    networkFee,
    showPaymentInstructions,
    showBurnInstructions,
    handleRequest,
    handlePaymentInstructionsProceed,
    handleBurnInstructionsProceed,
    handleConfirmRequest,
    handleChange,
    handleClose,
    handleOpenAssetMenu,
    handleCloseAssetMenu,
    chooseAsset,
    handleOpenChainMenu,
    handleCloseChainMenu,
    chooseChain,
    handleOpenFiatCurrencyMenu,
    handleCloseFiatCurrencyMenu,
    chooseFiatCurrency,
    selectedWallet,
    selectedWalletBalance,
    isOptedIn
  } = useRequest(type)
  const walletError = errors.walletError
  const isMint = type === RequestType.MINT
  const isBurn = type === RequestType.BURN
  // const isSendOther = type === RequestType.SEND_THIRD_PARTY

  const currentAsset = assets.find((item) => item.name === asset) as Asset
  const currentChain = chains.find((item) => item.name === selectedChain) as Chain
  const currentFiatCurrency = fiatCurrencies.find((item) => item.name === fiatCurrency) as Currency

  // networkFee and fxRate are now provided by useRequest hook

  // TO-DO: networkFee not being calculated currently in calculateFiatAmount method, update it when we start supporting networkFees
  const fiatAmount = value ? formatNumber(value) : 0

  const assetBalance =
    selectedWallet.chain == ChainName.ETHEREUM
      ? selectedWalletBalance && selectedWalletBalance[asset === ChainAsset.QCAD ? 'qcad' : 'usdc']
      : selectedWalletBalance && selectedWalletBalance[asset === ChainAsset.QCAD ? 'qcad_xlm' : 'usdc_xlm']

  return (
    <>
      {!order ? (
        <Container error={walletError} maxWidth={showPaymentInstructions ? '1000px' : '550px'}>
          {showPaymentInstructions && isMint ? (
            <PaymentInstructions
              amount={fiatAmount}
              fiatCurrency={fiatCurrency}
              onProceed={handlePaymentInstructionsProceed}
            />
          ) : showBurnInstructions && isBurn ? (
            <BurnInstructions selectedChain={selectedChain} asset={asset} onProceed={handleBurnInstructionsProceed} />
          ) : (
            <>
              <S.Row>
                <Typography variant="h5" fontWeight={600}>
                  {isMint && 'Mint Order'}
                  {isBurn && 'Burn Order'}
                </Typography>
              </S.Row>
              {
                <Stack>
                  <S.Row>
                    {selectedWalletBalance && (
                      <S.WalletBalance>
                        Balance: {assetBalance?.toString() ? formatNumber(assetBalance as number) : 'not opted-in'}
                      </S.WalletBalance>
                    )}
                    <S.InputLabel>Asset</S.InputLabel>
                    <TextField
                      value={value}
                      onChange={handleChange}
                      name="asset"
                      placeholder="0"
                      error={Boolean(errors.inputError)}
                      helperText={errors.inputError}
                      InputProps={{
                        inputComponent: NumberFormatCustom as unknown as React.ElementType<InputBaseComponentProps>,
                        endAdornment: (
                          <S.StyledInputAdornment position="end">
                            <Button
                              size="small"
                              startIcon={<currentAsset.Icon />}
                              endIcon={assets.length > 1 ? <ExpandMore /> : undefined}
                              color="inherit"
                              onClick={assets.length > 1 ? handleOpenAssetMenu : undefined}
                            >
                              {currentAsset.name}
                            </Button>
                            {assets.length > 1 && (
                              <Menu
                                id="menu-asset-type"
                                anchorEl={anchorElAsset}
                                keepMounted
                                open={Boolean(anchorElAsset)}
                                onClose={handleCloseAssetMenu}
                              >
                                {assets.map(({ name, Icon }) => (
                                  <MenuItem
                                    key={name}
                                    onClick={() => chooseAsset(name)}
                                    disabled={name === ChainAsset.QCAD && selectedWallet.chain === ChainName.ALGORAND}
                                  >
                                    <ListItemIcon>
                                      <Icon fontSize="small" />
                                    </ListItemIcon>
                                    <Typography>{name}</Typography>
                                  </MenuItem>
                                ))}
                              </Menu>
                            )}
                            {asset === ChainAsset.QCAD && (
                              <>
                                <Button
                                  size="small"
                                  startIcon={<currentChain.Icon />}
                                  endIcon={<ExpandMore />}
                                  color="inherit"
                                  onClick={handleOpenChainMenu}
                                >
                                  {currentChain.displayName}
                                </Button>
                                <Menu
                                  id="menu-chain-type"
                                  anchorEl={anchorElChain}
                                  keepMounted
                                  open={Boolean(anchorElChain)}
                                  onClose={handleCloseChainMenu}
                                >
                                  {chains.map((chain) => (
                                    <MenuItem key={chain.name} onClick={() => chooseChain(chain.name)}>
                                      <ListItemIcon>
                                        <chain.Icon fontSize="small" />
                                      </ListItemIcon>
                                      <Typography>{chain.displayName}</Typography>
                                    </MenuItem>
                                  ))}
                                </Menu>
                              </>
                            )}
                          </S.StyledInputAdornment>
                        )
                      }}
                      fullWidth
                      size="medium"
                    />
                    {isMint && (
                      <Typography variant="subtitle1" color="grey.700" sx={{ m: 1 }}>
                        <strong>Minting Wallet Address:</strong>{' '}
                        {selectedWallet.address ? trimId(selectedWallet.address) : 'N/A'}
                      </Typography>
                    )}
                  </S.Row>
                  <S.Row display="flex" justifyContent="center">
                    <FromToIcon sx={{ transform: isMint ? 'rotateX(180deg)' : undefined }} fontSize="large" />
                  </S.Row>
                  <S.Row>
                    <S.InputLabel>Amount</S.InputLabel>
                    <S.AmountTextField
                      onChange={handleChange}
                      name="amount"
                      placeholder="0"
                      value={value ? fiatAmount : ''}
                      InputProps={{
                        endAdornment: (
                          <S.StyledInputAdornment position="end">
                            <Button
                              size="small"
                              startIcon={<currentFiatCurrency.Icon />}
                              endIcon={fiatCurrencies.length > 1 ? <ExpandMore /> : undefined}
                              color="inherit"
                              onClick={fiatCurrencies.length > 1 ? handleOpenFiatCurrencyMenu : undefined}
                              disabled={isBurn}
                            >
                              {currentFiatCurrency.name}
                            </Button>
                            {fiatCurrencies.length > 1 && (
                              <Menu
                                id="menu-fiat-currency"
                                anchorEl={anchorElFiatCurrency}
                                keepMounted
                                open={Boolean(anchorElFiatCurrency)}
                                onClose={handleCloseFiatCurrencyMenu}
                              >
                                {fiatCurrencies.map(({ name, Icon }) => (
                                  <MenuItem key={name} onClick={() => chooseFiatCurrency(name)}>
                                    <ListItemIcon>
                                      <Icon fontSize="small" />
                                    </ListItemIcon>
                                    <Typography>{name}</Typography>
                                  </MenuItem>
                                ))}
                              </Menu>
                            )}
                          </S.StyledInputAdornment>
                        )
                      }}
                      disabled
                      fullWidth
                      size="medium"
                    />
                  </S.Row>
                  <S.StyledDivider />
                  <LoadingButton
                    variant="contained"
                    size="large"
                    loading={loading}
                    fullWidth
                    onClick={handleRequest}
                    disabled={
                      Boolean(errors.inputError) ||
                      (Number(value) === 0 && isOptedIn) ||
                      (asset === ChainAsset.QCAD && selectedWallet.chain === ChainName.ALGORAND) ||
                      (fiatCurrency === FiatCurrency.USD && isMint) ||
                      false // Blockchain details are always custodial, not myalgo
                    }
                  >
                    {isMint && 'Mint'}
                    {isBurn && 'Burn'}
                  </LoadingButton>
                </Stack>
              }
            </>
          )}
        </Container>
      ) : (
        <RequestInitiated order={order} type={type} networkFee={networkFee} />
      )}
      <ConfirmRequest
        open={modals.confirm}
        onClose={() => handleClose('confirm')}
        asset={currentAsset}
        cryptoAmount={value}
        fiatAmount={fiatAmount}
        networkFee={networkFee}
        confirmRequest={handleConfirmRequest}
        type={type}
        fiatCurrency={fiatCurrency}
        selectedWalletAddress={selectedWallet.address}
        selectedWalletChain={selectedWallet.chain}
      />
      <RequestLoading
        open={modals.requestLoading}
        asset={asset}
        cryptoAmount={value}
        fiatAmount={fiatAmount}
        type={type}
        fiatCurrency={fiatCurrency}
      />
    </>
  )
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
}

const NumberFormatCustom = React.forwardRef<NumberFormat<Record<string, unknown>>, CustomProps>((props, ref) => {
  const { onChange, ...other } = props

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        })
      }}
      thousandSeparator
      isNumericString
      allowNegative={false}
      decimalScale={2}
      fixedDecimalScale
    />
  )
})

NumberFormatCustom.displayName = 'NumberFormatCustom'

export default Request
